import is from '@sindresorhus/is';
import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { TranslationKey } from '~/shared/internationalization/translation';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import { SectionContent } from '../../components/Common';
import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { SelectionCommentButton } from './SelectionCommentButton';
import { stripCommentMarks } from './strip-comment-marks';
import { useActivateThread } from './use-activate-thread';
import { useResolvedSectionMarkedHtml } from './use-resolved-section-marked-html';
import { useTextSelection } from './use-text-selection';
import type { WrapSelectionResult } from './wrap-selection-in-mark';
import { wrapSelectionInMark } from './wrap-selection-in-mark';

const wrapSelectionFailureToTranslationKey = (
    reason: Extract<WrapSelectionResult, { readonly ok: false }>['reason'],
): TranslationKey => {
    switch (reason) {
        case 'cross-section':
            return 'pages.job-architecture-v2.comments.errors.cross-section';
        case 'collapsed':
        case 'empty':
            return 'pages.job-architecture-v2.comments.errors.select-text-first';
    }
};

const TitleText: FunctionComponent<{ readonly children: string }> = ({ children }) => (
    <div className="mb-xl text-sm font-medium capitalize leading-normal text-black-900">{children}</div>
);

type CommentableDescriptionSectionProps = {
    readonly itemId: string;
    readonly sectionKey: string;
    readonly sectionTitle: string;
    readonly html: string;
};

export const CommentableDescriptionSection: FunctionComponent<CommentableDescriptionSectionProps> = ({
    itemId,
    sectionKey,
    sectionTitle,
    html,
}) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const setPendingTarget = useActiveThreadStore((state) => state.setPendingTarget);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const setIsCommentsOpen = useActiveThreadStore((state) => state.setIsCommentsOpen);
    const setViewMode = useActiveThreadStore((state) => state.setViewMode);
    const activateThread = useActivateThread(itemId);
    const { selection, clearSelection } = useTextSelection(sectionRef);
    const { showErrorMessageSnackbar } = useSnackbars();
    const markedHtml = useResolvedSectionMarkedHtml(sectionKey, html);

    useEffect(() => {
        if (!is.null(selection)) {
            setActiveThreadId(null);
            setViewMode('active');
        }
    }, [selection, setActiveThreadId, setViewMode]);

    const innerHtmlProp = useMemo(() => ({ __html: markedHtml ?? html }), [html, markedHtml]);

    const handleClick = useCallback(
        (event: MouseEvent<HTMLDivElement>): void => {
            const target = event.target instanceof HTMLElement ? event.target : null;
            if (is.null(target)) return;
            const mark = target.closest<HTMLElement>('mark[data-comment-id]');
            if (is.null(mark)) return;
            const commentId = mark.getAttribute('data-comment-id');
            if (is.null(commentId)) return;
            setIsCommentsOpen(true);
            activateThread(commentId, { kind: 'description-section', sectionId: sectionKey });
        },
        [activateThread, sectionKey, setIsCommentsOpen],
    );

    const handleComment = (): void => {
        if (is.null(selection)) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey('collapsed'));
            return;
        }
        const range = selection.getRange();
        if (is.null(range)) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey('collapsed'));
            return;
        }
        const anchorRect = range.getBoundingClientRect();
        const sectionEl = sectionRef.current;
        if (is.null(sectionEl)) return;
        const sectionRoot = sectionEl.querySelector<HTMLElement>('[data-section-content-root="true"]');
        if (is.null(sectionRoot)) return;
        const id = crypto.randomUUID();
        const result = wrapSelectionInMark({ range, sectionRoot });
        if (!result.ok) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey(result.reason));
            return;
        }
        setIsCommentsOpen(true);
        setPendingTarget({
            target: { kind: 'description-section', sectionId: sectionKey },
            anchorRect,
            anchorEl: sectionEl,
            anchor: result.anchor,
            commentId: id,
            prevDetails: null,
        });
        clearSelection();
    };

    return (
        <section
            ref={sectionRef}
            data-anchor-kind="description-section"
            data-item-id={itemId}
            data-section-key={sectionKey}
            data-testid="commentable-description-section"
        >
            <TitleText>{sectionTitle}</TitleText>
            <SectionContent
                data-section-content-root="true"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich content with inline marks
                dangerouslySetInnerHTML={innerHtmlProp}
                data-testid="description-section-content"
                onClick={handleClick}
            />
            {is.null(selection) ? null : <SelectionCommentButton rect={selection.rect} onComment={handleComment} />}
        </section>
    );
};

type PlainDescriptionSectionProps = {
    readonly sectionTitle: string;
    readonly html: string;
};

export const PlainDescriptionSection: FunctionComponent<PlainDescriptionSectionProps> = ({ sectionTitle, html }) => {
    const innerHtmlProp = useMemo(() => ({ __html: stripCommentMarks(html) }), [html]);

    return (
        <>
            <TitleText>{sectionTitle}</TitleText>
            <SectionContent
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich content rendered in non-commentable contexts
                dangerouslySetInnerHTML={innerHtmlProp}
                data-testid="description-section-content"
            />
        </>
    );
};
